/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { effects } from "../../../../components/effects";
import { StyledText } from "../../../../assets/typography/typography";
import { VerticalDivider } from "../../../../components/VerticalDivider";
import {
  Listbox,
  ListboxOption,
  ListboxButton,
  ListboxOptions,
} from "@headlessui/react";
import { popoverPanelStyle } from "../../../../components/PopoverBase";
import { useController, useFormContext } from "react-hook-form";
import { AutoCampaignMutationPayload } from "../models/AutoCampaign";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { SelectableItem } from "../../../../components/SelectableItem";
import { colors } from "../../../../components/colors";
import { ErrorMessage } from "@hookform/error-message";

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const innerWrapperStyle = css`
  display: flex;
  flex-direction: row;
  gap: 0px;
  align-items: center;
  box-shadow: ${effects.cardRest};
  border-radius: 4px;
`;

const explainationStyle = css`
  display: flex;
  flex-direction: column;
  padding: 8px;
  min-width: fit-content;
`;

const inputStyle = css`
  border: none;
  outline: none;
  box-shadow: none;
  width: 100%;
`;

const optionsPanelStyle = css`
  width: var(--button-width);
  padding: 4px;
  gap: 4px;
  max-height: 400px;
  overflow-y: scroll;
`;

const removeButtonStyle = css`
  border: none;
  height: 100%;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 32px;
  background-color: ${colors.transparent};
  &:hover {
    background-color: ${colors.whiteHover};
  }
`;

type Props = {
  options: {
    label: string;
    value: string;
  }[];
  index: number;
  onRemove: () => void;
};

export const TriggerConfigField = ({ options, index, onRemove }: Props) => {
  const { control } = useFormContext<AutoCampaignMutationPayload>();

  const { field } = useController({
    control,
    name: `productGroupConfigurations.${index}.triggerCount`,
  });

  const {
    field: { onChange: setProductGroupIds, value: productGroupIds },
  } = useController({
    control,
    name: `productGroupConfigurations.${index}.productGroupIds`,
  });

  const firstProductGroup = (options || []).find(
    (option) => option.value === productGroupIds?.[0]
  );

  const listboxStyle = css`
    width: 100%;
    align-items: center;
    padding: 8px;
    overflow: hidden;
    display: flex;
    height: 100%;
    &:hover {
      background-color: ${colors.whiteHover};
    }
    flex-direction: row;
    justify-content: space-between;
    color: ${firstProductGroup ? colors.textPrimary : colors.textSecondary};
  `;

  const textStyle = css`
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  `;

  return (
    <div css={wrapperStyle}>
      <div css={innerWrapperStyle}>
        <div css={explainationStyle}>
          <input css={inputStyle} placeholder="4" {...field} />
        </div>
        <VerticalDivider height="100%" />
        <div css={explainationStyle}>
          <StyledText
            color={colors.textSecondary}
            className="bodyTextRegular mb-0"
          >
            product(s) from
          </StyledText>
        </div>
        <VerticalDivider height="100%" />
        <Listbox multiple value={productGroupIds} onChange={setProductGroupIds}>
          <ListboxButton as="div" css={listboxStyle}>
            <div css={textStyle}>
              {firstProductGroup?.label || "Select"}
              {productGroupIds.length > 1 &&
                `, + ${productGroupIds?.length - 1} other`}
            </div>
            <FontAwesomeIcon icon={solid("chevron-down")} />
          </ListboxButton>
          <ListboxOptions
            transition
            css={[popoverPanelStyle, optionsPanelStyle]}
            anchor="bottom"
          >
            {options.length > 0 ? (
              options.map((option) => (
                <ListboxOption key={option.value} value={option.value}>
                  {({ selected }) => (
                    <SelectableItem
                      selected={selected}
                      icon={
                        selected && <FontAwesomeIcon icon={solid("check")} />
                      }
                    >
                      {option.label}
                    </SelectableItem>
                  )}
                </ListboxOption>
              ))
            ) : (
              <StyledText className="bodySubtextRegular mb-0 text-center">
                No product groups created
              </StyledText>
            )}
          </ListboxOptions>
        </Listbox>
        <VerticalDivider height="100%" />
        <div css={explainationStyle}>
          <StyledText
            className="bodyTextRegular mb-0"
            color={colors.textSecondary}
          >
            is in user’s cart
          </StyledText>
        </div>
        <VerticalDivider height="100%" />
        <button
          css={[explainationStyle, removeButtonStyle]}
          type="button"
          onClick={onRemove}
        >
          <FontAwesomeIcon icon={solid("xmark")} />
        </button>
      </div>
      <ErrorMessage
        name={`productGroupConfigurations.${index}.triggerCount`}
        render={({ message }) => (
          <StyledText
            color={colors.errorDark}
            className="bodySubtextRegular mb-0"
          >
            {message}
          </StyledText>
        )}
      />
      <ErrorMessage
        name={`productGroupConfigurations.${index}.productGroupIds`}
        render={({ message }) => (
          <StyledText
            color={colors.errorDark}
            className="bodySubtextRegular mb-0"
          >
            {message}
          </StyledText>
        )}
      />
    </div>
  );
};
